import {
  message,
  Spin,
  Typography,
  Card,
  Space,
  Button,
  Row,
  Col,
  Segmented,
  Table,
  Tag,
  Tooltip as AntTooltip,
  Divider,
} from 'antd';
import {
  ShoppingOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ThunderboltOutlined,
  DownloadOutlined,
  PlusOutlined,
  DatabaseOutlined,
  ProjectOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import productApi from '../../api/product';
import newApi from '../../api/new';
import newCategoryApi from '../../api/new-category';
import productCategoryApi from '../../api/category';
import projectApi from '../../api/project'; // get
import partnerApi from '../../api/partner'; // get
import backupApi from '../../api/backup';

import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';

const { Title, Text } = Typography;

const fetchData = async (api, setData, setLoading, errorMsg) => {
  setLoading(true);
  try {
    const res = await api.get({ page: 1, limit: 9999 });
    setData(res?.data?.data?.items || []);
  } catch (error) {
    message.error(errorMsg);
    console.error(error);
  } finally {
    setLoading(false);
  }
};

function getCreatedAt(item) {
  const d = item?.createdAt || item?.createdAtDate;
  if (!d) return null;
  const t = dayjs(d);
  return t.isValid() ? t : null;
}

function countInRange(items, fromDayjs) {
  return (items || []).reduce((acc, it) => {
    const t = getCreatedAt(it);
    if (!t) return acc;
    return t.isAfter(fromDayjs) ? acc + 1 : acc;
  }, 0);
}

function buildDailySeries(items, days) {
  const end = dayjs().endOf('day');
  const start = end.subtract(days - 1, 'day').startOf('day');
  const map = new Map();

  for (let i = 0; i < days; i++) {
    const d = start.add(i, 'day');
    map.set(d.format('YYYY-MM-DD'), 0);
  }

  (items || []).forEach((it) => {
    const t = getCreatedAt(it);
    if (!t) return;
    if (t.isBefore(start) || t.isAfter(end)) return;
    const key = t.format('YYYY-MM-DD');
    map.set(key, (map.get(key) || 0) + 1);
  });

  return Array.from(map.entries()).map(([date, value]) => ({
    date,
    value,
    label: dayjs(date).format('DD/MM'),
  }));
}

const MetricCard = ({ title, value, icon, loading, link, trend, trendLabel, color = '#1677ff' }) => {
  const isUp = (trend || 0) >= 0;

  return (
    <Card
      hoverable
      style={{ borderRadius: 16, boxShadow: '0 6px 18px rgba(0,0,0,0.06)', padding: 16 }}
    >
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(22,119,255,0.10)',
              color,
              fontSize: 20,
            }}
          >
            {icon}
          </div>
          <div>
            <Text type="secondary">{title}</Text>
            <div style={{ marginTop: 4 }}>
              {loading ? (
                <Spin />
              ) : (
                <Title level={3} style={{ margin: 0 }}>
                  {value}
                </Title>
              )}
            </div>
          </div>
        </Space>

        <div style={{ textAlign: 'right' }}>
          <Tag
            color={isUp ? 'green' : 'red'}
            icon={isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            style={{ borderRadius: 999 }}
          >
            {loading ? '...' : `${Math.abs(trend)}%`}
          </Tag>
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {trendLabel}
            </Text>
          </div>
          <div style={{ marginTop: 6 }}>
            <Link to={link}>Xem chi tiết</Link>
          </div>
        </div>
      </Space>
    </Card>
  );
};

function Dashboard() {
  const [range, setRange] = useState(7); // 7/30/90

  const [products, setProducts] = useState([]);
  const [news, setNews] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [newsCategories, setNewsCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [loadingProductCategories, setLoadingProductCategories] = useState(false);
  const [loadingNewsCategories, setLoadingNewsCategories] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);

  // backup summary
  const [backupInfo, setBackupInfo] = useState({ count: 0, latest: null });
  const [loadingBackup, setLoadingBackup] = useState(false);

  const loadingAny =
    loadingProducts ||
    loadingNews ||
    loadingProductCategories ||
    loadingNewsCategories ||
    loadingProjects ||
    loadingPartners ||
    loadingBackup;

  const fetchBackupsSummary = async () => {
    try {
      setLoadingBackup(true);
      const res = await backupApi.list();
      const backups = res?.data?.backups || [];
      const latest = backups?.[0] || null;
      setBackupInfo({ count: backups.length, latest });
    } catch (e) {
      console.error(e);
      setBackupInfo({ count: 0, latest: null });
    } finally {
      setLoadingBackup(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([
      fetchData(productApi, setProducts, setLoadingProducts, 'Lỗi khi tải sản phẩm'),
      fetchData(newApi, setNews, setLoadingNews, 'Lỗi khi tải bài viết'),
      fetchData(newCategoryApi, setNewsCategories, setLoadingNewsCategories, 'Lỗi khi tải danh mục tin tức'),
      fetchData(productCategoryApi, setProductCategories, setLoadingProductCategories, 'Lỗi khi tải danh mục sản phẩm'),
      fetchData(projectApi, setProjects, setLoadingProjects, 'Lỗi khi tải dự án'),
      fetchData(partnerApi, setPartners, setLoadingPartners, 'Lỗi khi tải đối tác'),
      fetchBackupsSummary(),
    ]);
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trend
  const trendPack = useMemo(() => {
    const nowFrom = dayjs().subtract(range, 'day');
    const prevFrom = dayjs().subtract(range * 2, 'day');
    const prevTo = dayjs().subtract(range, 'day');

    const calcTrend = (items) => {
      const curr = countInRange(items, nowFrom);
      const prev = (items || []).reduce((acc, it) => {
        const t = getCreatedAt(it);
        if (!t) return acc;
        if (t.isAfter(prevFrom) && t.isBefore(prevTo)) return acc + 1;
        return acc;
      }, 0);

      if (prev === 0 && curr === 0) return { curr, prev, pct: 0 };
      if (prev === 0 && curr > 0) return { curr, prev, pct: 100 };
      const pct = Math.round(((curr - prev) / prev) * 100);
      return { curr, prev, pct };
    };

    return {
      products: calcTrend(products),
      news: calcTrend(news),
      productCategories: calcTrend(productCategories),
      newsCategories: calcTrend(newsCategories),
      projects: calcTrend(projects),
      partners: calcTrend(partners),
    };
  }, [products, news, productCategories, newsCategories, projects, partners, range]);

  const chartData = useMemo(
    () => [
      { name: 'Sản phẩm', value: products.length },
      { name: 'Bài viết', value: news.length },
      { name: 'DM sản phẩm', value: productCategories.length },
      { name: 'DM tin tức', value: newsCategories.length },
      { name: 'Dự án', value: projects.length },
      { name: 'Đối tác', value: partners.length },
    ],
    [products.length, news.length, productCategories.length, newsCategories.length, projects.length, partners.length]
  );

  const dailySeries = useMemo(() => {
    const p = buildDailySeries(products, Math.min(range, 30));
    const n = buildDailySeries(news, Math.min(range, 30));
    const prj = buildDailySeries(projects, Math.min(range, 30));

    const map = new Map();
    p.forEach((x) =>
      map.set(x.date, { date: x.date, label: x.label, products: x.value, news: 0, projects: 0 })
    );
    n.forEach((x) => {
      const cur = map.get(x.date) || { date: x.date, label: x.label, products: 0, news: 0, projects: 0 };
      map.set(x.date, { ...cur, news: x.value, label: x.label });
    });
    prj.forEach((x) => {
      const cur = map.get(x.date) || { date: x.date, label: x.label, products: 0, news: 0, projects: 0 };
      map.set(x.date, { ...cur, projects: x.value, label: x.label });
    });

    return Array.from(map.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
  }, [products, news, projects, range]);

  const latestProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => (getCreatedAt(b)?.valueOf() || 0) - (getCreatedAt(a)?.valueOf() || 0))
      .slice(0, 6);
  }, [products]);

  const latestNews = useMemo(() => {
    return [...news]
      .sort((a, b) => (getCreatedAt(b)?.valueOf() || 0) - (getCreatedAt(a)?.valueOf() || 0))
      .slice(0, 6);
  }, [news]);

  const latestProjects = useMemo(() => {
    return [...projects]
      .sort((a, b) => (getCreatedAt(b)?.valueOf() || 0) - (getCreatedAt(a)?.valueOf() || 0))
      .slice(0, 6);
  }, [projects]);

  const latestPartners = useMemo(() => {
    return [...partners]
      .sort((a, b) => (getCreatedAt(b)?.valueOf() || 0) - (getCreatedAt(a)?.valueOf() || 0))
      .slice(0, 6);
  }, [partners]);

  const productColumns = [
    {
      title: 'Sản phẩm',
      dataIndex: 'names',
      key: 'names',
      render: (v, r) => <span>{v?.vi || v?.en || r?.name || '—'}</span>,
    },
    {
      title: 'Ngày tạo',
      key: 'createdAt',
      width: 170,
      render: (_, r) => {
        const t = getCreatedAt(r);
        return <span>{t ? t.format('DD-MM-YYYY HH:mm') : '—'}</span>;
      },
    },
  ];

  const newsColumns = [
    {
      title: 'Bài viết',
      dataIndex: 'names',
      key: 'names',
      render: (v, r) => <span>{v?.vi || v?.en || r?.title || '—'}</span>,
    },
    {
      title: 'Ngày tạo',
      key: 'createdAt',
      width: 170,
      render: (_, r) => {
        const t = getCreatedAt(r);
        return <span>{t ? t.format('DD-MM-YYYY HH:mm') : '—'}</span>;
      },
    },
  ];

  const projectColumns = [
    {
      title: 'Dự án',
      dataIndex: 'names',
      key: 'names',
      render: (v, r) => <span>{v?.vi || v?.en || r?.name || '—'}</span>,
    },
    {
      title: 'Ngày tạo',
      key: 'createdAt',
      width: 170,
      render: (_, r) => {
        const t = getCreatedAt(r);
        return <span>{t ? t.format('DD-MM-YYYY HH:mm') : '—'}</span>;
      },
    },
  ];

  const partnerColumns = [
    {
      title: 'Đối tác',
      dataIndex: 'names',
      key: 'names',
      render: (v, r) => <span>{v?.vi || v?.en || r?.name || '—'}</span>,
    },
    {
      title: 'Ngày tạo',
      key: 'createdAt',
      width: 170,
      render: (_, r) => {
        const t = getCreatedAt(r);
        return <span>{t ? t.format('DD-MM-YYYY HH:mm') : '—'}</span>;
      },
    },
  ];

  // Backup actions
  const handleBackupNow = async () => {
    try {
      setLoadingBackup(true);
      await backupApi.create();
      message.success('Tạo backup thành công');
      await fetchBackupsSummary();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || 'Tạo backup thất bại');
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleDownloadLatestBackup = async () => {
    try {
      setLoadingBackup(true);
      // dùng downloadLatest (blob) nếu bạn implement như mình gửi trước
      const res = await backupApi.downloadLatest();
      const filename =
        backupInfo?.latest?.filename || `latest_backup_${dayjs().format('YYYY-MM-DD_HH-mm')}.archive.gz`;

      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/gzip',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      message.success('Đã tải backup mới nhất');
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || 'Tải backup mới nhất thất bại');
    } finally {
      setLoadingBackup(false);
    }
  };

  const latestBackupText = useMemo(() => {
    const latest = backupInfo.latest;
    if (!latest) return 'Chưa có backup';
    const t = latest.updatedAt ? dayjs(latest.updatedAt) : null;
    return `${latest.filename}${t ? ` • ${t.format('DD-MM-YYYY HH:mm')}` : ''}`;
  }, [backupInfo]);

  return (
    <>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Dashboard
          </Title>
          <Text type="secondary">Tổng quan hoạt động hệ thống & các mục quản trị</Text>
        </div>

        <Space>
          <AntTooltip title="Khoảng thời gian dùng để tính trend và biểu đồ theo ngày">
            <Segmented
              value={range}
              onChange={(v) => setRange(v)}
              options={[
                { label: '7 ngày', value: 7 },
                { label: '30 ngày', value: 30 },
                { label: '90 ngày', value: 90 },
              ]}
            />
          </AntTooltip>
          <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={loadingAny}>
            Làm mới
          </Button>
        </Space>
      </Space>

      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8} xl={6}>
          <MetricCard
            title="Sản phẩm"
            value={products.length}
            loading={loadingProducts}
            icon={<ShoppingOutlined />}
            link="/admin/products"
            trend={trendPack.products.pct}
            trendLabel={`So với ${range} ngày trước`}
          />
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6}>
          <MetricCard
            title="Bài viết"
            value={news.length}
            loading={loadingNews}
            icon={<FileTextOutlined />}
            link="/admin/news"
            trend={trendPack.news.pct}
            trendLabel={`So với ${range} ngày trước`}
          />
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6}>
          <MetricCard
            title="Dự án"
            value={projects.length}
            loading={loadingProjects}
            icon={<ProjectOutlined />}
            link="/admin/projects"
            trend={trendPack.projects.pct}
            trendLabel={`So với ${range} ngày trước`}
          />
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6}>
          <MetricCard
            title="Đối tác"
            value={partners.length}
            loading={loadingPartners}
            icon={<TeamOutlined />}
            link="/admin/partner"
            trend={trendPack.partners.pct}
            trendLabel={`So với ${range} ngày trước`}
          />
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6}>
          <MetricCard
            title="Danh mục SP"
            value={productCategories.length}
            loading={loadingProductCategories}
            icon={<AppstoreOutlined />}
            link="/admin/product-category"
            trend={trendPack.productCategories.pct}
            trendLabel={`So với ${range} ngày trước`}
          />
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6}>
          <MetricCard
            title="Danh mục tin"
            value={newsCategories.length}
            loading={loadingNewsCategories}
            icon={<TagsOutlined />}
            link="/admin/news-category"
            trend={trendPack.newsCategories.pct}
            trendLabel={`So với ${range} ngày trước`}
          />
        </Col>

        {/* Quick action card */}
        <Col xs={24} sm={12} lg={8} xl={6}>
          <Card
            hoverable
            style={{ borderRadius: 16, boxShadow: '0 6px 18px rgba(0,0,0,0.06)', padding: 16 }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(22,119,255,0.10)',
                      color: '#1677ff',
                      fontSize: 20,
                    }}
                  >
                    <ThunderboltOutlined />
                  </div>
                  <div>
                    <Text type="secondary">Quick actions</Text>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        Backup DB
                      </Title>
                    </div>
                  </div>
                </Space>

                <Tag color="blue" style={{ borderRadius: 999 }}>
                  {loadingBackup ? '...' : `${backupInfo.count} file`}
                </Tag>
              </Space>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Latest: {loadingBackup ? 'đang tải...' : latestBackupText}
              </Text>

              <Space wrap>
                <Button icon={<PlusOutlined />} type="primary" onClick={handleBackupNow} loading={loadingBackup}>
                  Backup ngay
                </Button>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadLatestBackup} loading={loadingBackup}>
                  Tải latest
                </Button>
                <Link to="/admin/backup">
                  <Button icon={<DatabaseOutlined />}>Quản lý backup</Button>
                </Link>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Charts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <Card title="Tổng quan số lượng" extra={<Text type="secondary">Realtime</Text>} style={{ borderRadius: 16 }}>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#1677ff" barSize={24} radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={14}>
          <Card
            title="Xu hướng theo ngày (tối đa 30 ngày hiển thị)"
            extra={<Text type="secondary">{`Đang xem: ${range} ngày`}</Text>}
            style={{ borderRadius: 16 }}
          >
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailySeries} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="products" name="Sản phẩm" stroke="#1677ff" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="news" name="Bài viết" stroke="#52c41a" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="projects" name="Dự án" stroke="#faad14" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Latest tables */}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="Sản phẩm mới nhất" extra={<Link to="/admin/products">Xem tất cả</Link>} style={{ borderRadius: 16 }}>
            <Table size="small" columns={productColumns} dataSource={latestProducts} rowKey={(r) => r._id || r.id} pagination={false} loading={loadingProducts} />
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card title="Bài viết mới nhất" extra={<Link to="/admin/news">Xem tất cả</Link>} style={{ borderRadius: 16 }}>
            <Table size="small" columns={newsColumns} dataSource={latestNews} rowKey={(r) => r._id || r.id} pagination={false} loading={loadingNews} />
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card title="Dự án mới nhất" extra={<Link to="/admin/projects">Xem tất cả</Link>} style={{ borderRadius: 16 }}>
            <Table size="small" columns={projectColumns} dataSource={latestProjects} rowKey={(r) => r._id || r.id} pagination={false} loading={loadingProjects} />
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card title="Đối tác mới nhất" extra={<Link to="/admin/partner">Xem tất cả</Link>} style={{ borderRadius: 16 }}>
            <Table size="small" columns={partnerColumns} dataSource={latestPartners} rowKey={(r) => r._id || r.id} pagination={false} loading={loadingPartners} />
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default Dashboard;
